import React, { useState } from "react";
import Button from "../../../shared/components/UI/Button";
import Input from "../../../shared/components/UI/Input";
import Form from "../../../shared/components/UI/Form";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { useMutation } from "@tanstack/react-query";
import authService from "../services/authService";
import { AxiosError } from "axios";
import { displayNoConnection, displayNotify } from "../../../shared/services/Notifications/NotificationServices";
import "../styles/LoginForm.scss";
import { RegisterPageState, ILoginForm } from "../utils/types";

interface Props {
  registerPageState: RegisterPageState;
  setRegisterPageState: React.Dispatch<React.SetStateAction<RegisterPageState>>;
}

const loginNotificationMap: Record<
  number,
  { title: string; description: string }
> = {
  401: {
    title: "Ошибка входа",
    description: "Неверный логин или пароль",
  },
  403: {
    title: "Аккаунт заблокирован",
    description: "Обратитесь к администратору для разблокировки.",
  },
  422: {
    title: "Ошибка валидации",
    description: "Некорректные данные. Проверьте форму.",
  },
  429: {
    title: "Слишком много попыток",
    description: "Подождите немного перед следующей попыткой входа.",
  },
  500: {
    title: "Ошибка сервера",
    description: "Что-то пошло не так. Попробуйте позже.",
  },
  504: {
    title: "Нет соединения",
    description: "Проверьте интернет или попробуйте позже.",
  }
};

function LoginForm({ registerPageState, setRegisterPageState }: Props) {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate();
  const redirect = searchParams.get('return_to') || '/'

  const { register, handleSubmit, getValues, trigger, watch } = useForm<ILoginForm>({
    mode: "onChange",
  });


  const loginM = useMutation({
    mutationFn: (form: ILoginForm) => authService.authFetch(form),
    onSuccess: () => {
      displayNotify({
        status: "success",
        title: "Успешный вход",
        description: "Вы успешно вошли в систему",
      });
      navigate(redirect);
    },
    onError: (error: AxiosError) => {
      if (!error.response) {
        displayNoConnection()
        return;
      }

      const status = error.response.status;
      const detail = (error.response.data as any)?.detail;

      if (detail?.includes("CSRF Failed")) {
        displayNotify({
          status: "error",
          title: "Ошибка безопасности",
          description: "CSRF ошибка. Обновите страницу и попробуйте снова.",
        });
        return;
      }

      if (typeof detail === "string" && detail.includes("Уже авторизован")) {
        displayNotify({
          status: "error",
          title: "Вы уже в системе",
          description:
            "Вы уже вошли ранее. Обновите страницу, если нужно повторно войти.",
        });
        return;
      }

      const message = loginNotificationMap[status];

      displayNotify({
        status: "error",
        title: message?.title || "Неизвестная ошибка",
        description:
          message?.description || detail || "Попробуйте снова позже.",
      });
    },
  });

  const onSubmit: SubmitHandler<ILoginForm> = async () => {
    const values = getValues();

    if (!values.login || values.login.trim() === "") {
      displayNotify({
        status: "error",
        title: 'Ошибка в поле "Логин"',
        description: "Поле логина обязательно для заполнения",
      });
      return;
    }

    if (!values.password || values.password.trim() === "") {
      displayNotify({
        status: "error",
        title: 'Ошибка в поле "Пароль"',
        description: "Поле пароля обязательно для заполнения",
      });
      return;
    }

    // Если всё ок — можно запускать триггер для доп. валидаций
    const valid = await trigger();

    if (!valid) {
      displayNotify({
        status: "error",
        title: "Ошибка валидации",
        description: "Проверьте корректность введённых данных.",
      });
      return;
    }

    loginM.mutate(values);
  };


  const onRegisterClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setRegisterPageState("register");
  };

  const onPasswordChangeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setRegisterPageState("password-change");
  }

  const [isAgreementOpen, setAgreementOpen] = useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  const touchedPassword = watch('password')

  const eye =
    <img
      src={`/${isPasswordVisible ? 'close' : 'open'}-password.svg`}
      className={`login_input-eye ${touchedPassword ? '' : 'login_input-eye--hidden'}`}
      onClick={() => touchedPassword ? (isPasswordVisible ? setIsPasswordVisible(false) : setIsPasswordVisible(true)) : null}
    />

  return (
    <div
      className="login-page"
      style={{ backgroundColor: 'rgba(12, 12, 12, 0.5)', position: "absolute", minHeight: "100vh", overflow: "hidden" }}
    >
      <div
        className="login-container"
        style={{ position: "relative", zIndex: 1 }}
      >
        <Form
          noValidate
          className="login_form"
          onSubmit={handleSubmit(onSubmit)}
          style={{
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: "1px solid #29292A",
            borderRadius: "20px",
          }}
        >
          <div className="login_header">
            <img className="login_logo" src="/skull.svg" alt="logo" />
            <h2 className="login_title">Доброго дня, комрад!</h2>
            <p className="login_subtitle">Введите данные для входа в систему</p>
          </div>

          <div className="login_fields">
            <div className="login_input-wrapper">
              <Input
                type="text"
                placeholder="Логин или почта"
                autoComplete="username"
                className="login_input"
                {...register("login")}
              />
            </div>

            <div className="login_input-wrapper">
              <Input
                type={isPasswordVisible ? "text" : "password"}
                placeholder="Пароль"
                className="login_input"
                {...register("password")}
                rightElement={() => eye}
              />
            </div>
          </div>

          <div className="login_actions">
            <Button
              contraststyle="true"
              type="submit"
              disabled={loginM.isPending}
              className="login_submit"
            >
              {loginM.isPending ? "Входим..." : "Продолжить"}
            </Button>
            <p className="forgot-password">Нет аккаунта? - <a href="#" onClick={onRegisterClick}>
              Зарегистрироваться
            </a></p>
            <p className="forgot-password">Забыли пароль? - <a href="#" onClick={onPasswordChangeClick}>
              Восстановить
            </a></p>
          </div>
        </Form>
      </div>
    </div>
  );
}

export default LoginForm;
